import { defineStore } from 'pinia'
import { report } from '@/libs/api'
import {
  ReportItemDecorator,
  ReportListDecorator,
} from '@/components/reports/decorators/ReportsListDecorator'

/**
 * Normalize timestamp to Unix seconds.
 * Threshold 1e10 distinguishes ms from seconds for all dates from 1970 to ~2286:
 * - Unix seconds today: ~1.75e9 (below 1e10)
 * - Unix ms today:     ~1.75e12 (above 1e10)
 *
 * @param {number|string} ts
 * @returns {number|null}
 */
const toUnixSeconds = (ts) => {
  const num = Number(ts)
  if (Number.isNaN(num)) return null
  return num > 1e10 ? Math.trunc(num / 1000) : Math.trunc(num)
}

export const useReports = defineStore('reports', {
  state: () => ({
    reports: [],
  }),
  actions: {
    /**
     * Load list of reports
     *
     * @returns {Promise<AxiosResponse<any>>}
     */
    getReports() {
      return report.getList({}).then(({ data }) => {
        data.sort((a, b) => b.createdAt - a.createdAt)
        this.reports = ReportListDecorator(data)
      })
    },
    /**
     * Add report to the list.
     * Normalizes createdAt from ms (WS) to Unix seconds (REST format expected by decorator).
     *
     * @param {object} rawData - WS event payload
     */
    addReport(rawData) {
      const normalizedData = {
        ...rawData,
        createdAt: toUnixSeconds(rawData.createdAt),
      }
      this.reports.unshift(ReportItemDecorator(normalizedData))
    },
    /**
     * Update report in store by id.
     * Used on wallet.report SUCCESS to apply url and status from WS.
     *
     * @param {string} id - report hash id
     * @param {object} changes - fields to merge into the raw model
     */
    updateReportById(id, changes) {
      const index = this.reports.findIndex((r) => r.id === id)
      if (index !== -1) {
        const updatedModel = { ...this.reports[index].model, ...changes }
        this.reports[index] = ReportItemDecorator(updatedModel)
      }
    },
    /**
     * Delete report from list by id
     *
     * @param {string} id - report hash id
     */
    deleteReportById(id) {
      const index = this.reports.findIndex((i) => i.id === id)
      if (index !== -1) {
        this.reports.splice(index, 1)
      }
    },
  },
  getters: {
    getReportById(state) {
      return (id) => state.reports.find((i) => i.id === id)
    },
  },
})
